import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { API_URL } from '../../config';
import { toApiError } from '../../lib/api-error';
import type { PageSkill, PageSkillEdit, PageSkillListRow, PageSkillVersion } from './types';

const jsonHeaders = { 'Content-Type': 'application/json' };

const listKey = (propertyId: string, pathname?: string) =>
  ['page-skills', propertyId, pathname ?? 'all'] as const;

// ── Queries ──────────────────────────────────────────────────────────────

/**
 * Skills for a property. Pass `pathname` (a path or full URL) to get only the
 * skills that would actually be presented on that page — the server applies the
 * same matcher the runtime uses, so this can't drift from what the agent sees.
 */
export function usePageSkills(
  propertyId: string,
  options?: { pathname?: string; includeArchived?: boolean; enabled?: boolean }
) {
  const { pathname, includeArchived, enabled = true } = options ?? {};
  return useQuery({
    queryKey: [...listKey(propertyId, pathname), { includeArchived: !!includeArchived }],
    enabled,
    queryFn: async (): Promise<PageSkillListRow[]> => {
      const params = new URLSearchParams();
      if (includeArchived) params.set('includeArchived', '1');
      if (pathname) params.set('pathname', pathname);
      const qs = params.toString() ? `?${params}` : '';
      const res = await fetch(`${API_URL}/org/properties/${propertyId}/page-skills${qs}`, {
        credentials: 'include',
      });
      if (!res.ok) throw await toApiError(res, 'Failed to load page skills');
      return (await res.json()).data;
    },
  });
}

/** One skill including its body. Only fetched when a skill is opened. */
export function usePageSkill(propertyId: string, skillId: string | null) {
  return useQuery({
    queryKey: ['page-skill', propertyId, skillId],
    enabled: !!skillId,
    queryFn: async (): Promise<PageSkill> => {
      const res = await fetch(`${API_URL}/org/properties/${propertyId}/page-skills/${skillId}`, {
        credentials: 'include',
      });
      if (!res.ok) throw await toApiError(res, 'Failed to load page skill');
      return (await res.json()).data;
    },
  });
}

export function usePageSkillVersions(propertyId: string, skillId: string | null) {
  return useQuery({
    queryKey: ['page-skill-versions', propertyId, skillId],
    enabled: !!skillId,
    queryFn: async (): Promise<PageSkillVersion[]> => {
      const res = await fetch(
        `${API_URL}/org/properties/${propertyId}/page-skills/${skillId}/versions`,
        { credentials: 'include' }
      );
      if (!res.ok) throw await toApiError(res, 'Failed to load version history');
      return (await res.json()).data;
    },
  });
}

// ── Mutations ────────────────────────────────────────────────────────────

/** Invalidate every view of this property's skills plus the opened skill. */
function invalidateSkill(
  qc: ReturnType<typeof useQueryClient>,
  propertyId: string,
  skillId?: string
) {
  qc.invalidateQueries({ queryKey: ['page-skills', propertyId] });
  if (skillId) {
    qc.invalidateQueries({ queryKey: ['page-skill', propertyId, skillId] });
    qc.invalidateQueries({ queryKey: ['page-skill-versions', propertyId, skillId] });
  }
}

/**
 * Edit a skill's content. `baseVersion` is required: if the browser agent wrote a
 * new version while the editor was open the server returns
 * PAGE_SKILL_VERSION_CONFLICT rather than silently discarding that write, and the
 * dialog re-reads instead of retrying blind.
 */
export function useUpdatePageSkill(propertyId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ skillId, ...edit }: PageSkillEdit & { skillId: string }) => {
      const res = await fetch(`${API_URL}/org/properties/${propertyId}/page-skills/${skillId}`, {
        method: 'PUT',
        credentials: 'include',
        headers: jsonHeaders,
        body: JSON.stringify(edit),
      });
      if (!res.ok) throw await toApiError(res, 'Failed to save page skill');
      return (await res.json()).data as PageSkill;
    },
    onSuccess: (saved) => invalidateSkill(qc, propertyId, saved.id),
  });
}

export function useArchivePageSkill(propertyId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (skillId: string) => {
      const res = await fetch(
        `${API_URL}/org/properties/${propertyId}/page-skills/${skillId}/archive`,
        { method: 'POST', credentials: 'include' }
      );
      if (!res.ok) throw await toApiError(res, 'Failed to archive page skill');
      return skillId;
    },
    onSuccess: (skillId) => invalidateSkill(qc, propertyId, skillId),
  });
}

export function useUnarchivePageSkill(propertyId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (skillId: string) => {
      const res = await fetch(
        `${API_URL}/org/properties/${propertyId}/page-skills/${skillId}/unarchive`,
        { method: 'POST', credentials: 'include' }
      );
      if (!res.ok) throw await toApiError(res, 'Failed to restore page skill');
      return skillId;
    },
    onSuccess: (skillId) => invalidateSkill(qc, propertyId, skillId),
  });
}
